/**
 * Notepad Preferences Hook
 * 
 * Manages user preferences for notepad behavior
 * Stored at /users/{uid}/private/notepadPrefs
 */

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmMode, NotepadPreferences } from '@/types/notepad';
import { logger } from "@/lib/logger";

const DEFAULT_DELETE_MODE: DeleteConfirmMode = 'inline';

/**
 * Hook to get and set the delete confirmation mode
 */
export function useDeleteConfirmMode() {
  const { user } = useAuth();
  const [mode, setMode] = useState<DeleteConfirmMode>(DEFAULT_DELETE_MODE);
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from Firestore
  useEffect(() => {
    async function loadPreference() {
      if (!user?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        const prefsRef = doc(db, 'users', user.uid, 'private', 'notepadPrefs');
        const prefsSnap = await getDoc(prefsRef);

        if (prefsSnap.exists()) {
          const prefs = prefsSnap.data() as NotepadPreferences;
          if (prefs.deleteConfirmMode) {
            setMode(prefs.deleteConfirmMode);
          }
        } else {
          // Fallback to localStorage
          const stored = localStorage.getItem('notepad:deleteConfirmMode');
          if (stored === 'inline' || stored === 'undoOnly') {
            setMode(stored);
          }
        }
      } catch (error) {
        logger.error('[useDeleteConfirmMode] Failed to load preference:', error);
        // Fallback to localStorage
        const stored = localStorage.getItem('notepad:deleteConfirmMode');
        if (stored === 'inline' || stored === 'undoOnly') {
          setMode(stored);
        }
      } finally {
        setIsLoading(false);
      }
    }

    void loadPreference();
  }, [user?.uid]);

  // Update preference
  const updateMode = async (newMode: DeleteConfirmMode) => {
    setMode(newMode);

    // Save to localStorage immediately
    localStorage.setItem('notepad:deleteConfirmMode', newMode);

    // Save to Firestore in background
    if (user?.uid) {
      try {
        const prefsRef = doc(db, 'users', user.uid, 'private', 'notepadPrefs');
        await setDoc(prefsRef, { deleteConfirmMode: newMode }, { merge: true });
      } catch (error) {
        logger.error('[useDeleteConfirmMode] Failed to save preference:', error);
      }
    }
  };

  return {
    mode,
    updateMode,
    isLoading,
    isInlineMode: mode === 'inline',
    isUndoOnlyMode: mode === 'undoOnly',
  };
}
